import { useState, useCallback, ChangeEvent, FocusEvent, FormEvent } from 'react';
import type { 
  ValidatorFunction, 
  ValidationSchema, 
  FieldState, 
  PasswordStrength,
  UseFormValidationReturn
} from '@/types';

// ============================================
// VALIDATORS
// ============================================

export const validators = {
  required: (message = 'Este campo es requerido'): ValidatorFunction => 
    (value: string): string | null => {
      if (!value || !value.trim()) {
        return message;
      }
      return null;
    },

  email: (message = 'Ingresa un correo electrónico válido'): ValidatorFunction => 
    (value: string): string | null => {
      if (!value) return null;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return message;
      }
      return null;
    },

  minLength: (min: number, message?: string): ValidatorFunction => 
    (value: string): string | null => {
      if (!value) return null;
      if (value.length < min) {
        return message || `Debe tener al menos ${min} caracteres`;
      }
      return null;
    },

  maxLength: (max: number, message?: string): ValidatorFunction => 
    (value: string): string | null => {
      if (!value) return null;
      if (value.length > max) {
        return message || `Debe tener máximo ${max} caracteres`;
      }
      return null;
    },

  pattern: (regex: RegExp, message: string): ValidatorFunction => 
    (value: string): string | null => {
      if (!value) return null;
      if (!regex.test(value)) {
        return message;
      }
      return null;
    },

  // Password strength validation
  password: (value: string): string[] | null => {
    if (!value) return null;
    
    const errors: string[] = [];
    
    if (value.length < 8) {
      errors.push('Mínimo 8 caracteres');
    }
    if (!/[A-Z]/.test(value)) {
      errors.push('Una letra mayúscula');
    }
    if (!/[a-z]/.test(value)) {
      errors.push('Una letra minúscula');
    }
    if (!/[0-9]/.test(value)) {
      errors.push('Un número');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      errors.push('Un carácter especial (!@#$%^&*)');
    }

    return errors.length > 0 ? errors : null;
  },

  // Match another field (for confirm password)
  match: (fieldName: string, message?: string): ValidatorFunction => 
    (value: string, formValues?: Record<string, unknown>): string | null => {
      if (!value) return null;
      if (formValues && value !== formValues[fieldName]) {
        return message || 'Los valores no coinciden';
      }
      return null;
    }
};

// ============================================
// PASSWORD STRENGTH CALCULATOR
// ============================================

export const getPasswordStrength = (password: string): PasswordStrength => {
  if (!password) return { score: 0, label: '', color: '' };

  let score = 0;
  
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

  if (score <= 2) return { score: 1, label: 'Débil', color: 'weak' };
  if (score <= 4) return { score: 2, label: 'Media', color: 'medium' };
  return { score: 3, label: 'Fuerte', color: 'strong' };
};

// ============================================
// CUSTOM HOOK
// ============================================

export function useFormValidation<T extends Record<string, string>>(
  initialValues: T,
  validationSchema: ValidationSchema
): UseFormValidationReturn<T> {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string | null>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Validate a single field
  const validateField = useCallback((
    name: keyof T, 
    value: string
  ): string | null => {
    const fieldValidators = validationSchema[name as string];
    if (!fieldValidators) return null;

    for (const validator of fieldValidators) {
      const result = validator(value, values as Record<string, unknown>);
      
      if (result) {
        return Array.isArray(result) ? result[0] ?? null : result;
      }
    }
    return null;
  }, [validationSchema, values]);

  // Validate all fields
  const validateAll = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof T, string | null>> = {};
    let isValid = true;

    Object.keys(validationSchema).forEach(name => {
      const key = name as keyof T;
      const error = validateField(key, values[key] ?? '');
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [validationSchema, values, validateField]);

  // Handle field change - progressive validation
  const handleChange = useCallback((
    e: ChangeEvent<HTMLInputElement>
  ): void => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? String(checked) : value;
    const key = name as keyof T;

    setValues(prev => ({ ...prev, [key]: newValue } as T));

    // Only validate if field has been touched
    if (touched[key]) {
      const error = validateField(key, newValue);
      setErrors(prev => ({ ...prev, [key]: error }));
    }
  }, [touched, validateField]);

  // Handle field blur - mark as touched and validate
  const handleBlur = useCallback((
    e: FocusEvent<HTMLInputElement>
  ): void => {
    const { name, value } = e.target;
    const key = name as keyof T;

    setTouched(prev => ({ ...prev, [key]: true }));
    
    const error = validateField(key, value);
    setErrors(prev => ({ ...prev, [key]: error }));
  }, [validateField]);

  // Handle form submission
  const handleSubmit = useCallback((
    onSubmit: (values: T) => Promise<void>
  ) => async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched: Partial<Record<keyof T, boolean>> = {};
    Object.keys(validationSchema).forEach(name => {
      allTouched[name as keyof T] = true;
    });
    setTouched(allTouched);

    // Validate all fields
    const isValid = validateAll();
    
    if (isValid && onSubmit) {
      setIsSubmitting(true);
      try {
        await onSubmit(values);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [validationSchema, validateAll, values]);

  // Reset form
  const reset = useCallback((): void => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  // Set field value programmatically
  const setValue = useCallback((name: keyof T, value: string): void => {
    setValues(prev => ({ ...prev, [name]: value } as T));
  }, []);

  // Set field error programmatically
  const setError = useCallback((name: keyof T, error: string | null): void => {
    setErrors(prev => ({ ...prev, [name]: error }));
  }, []);

  // Get field props
  const getFieldProps = useCallback((name: keyof T) => ({
    name,
    value: values[name] ?? '',
    onChange: handleChange,
    onBlur: handleBlur
  }), [values, handleChange, handleBlur]);

  // Get field state
  const getFieldState = useCallback((name: keyof T): FieldState => ({
    value: values[name] ?? '',
    error: errors[name] ?? null,
    touched: touched[name] ?? false,
    isValid: (touched[name] ?? false) && !errors[name] && !!values[name],
    isInvalid: (touched[name] ?? false) && !!errors[name]
  }), [values, errors, touched]);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    validateField,
    validateAll,
    reset,
    setValue,
    setError,
    getFieldProps,
    getFieldState,
    isValid: Object.keys(errors).every(key => !errors[key as keyof T])
  };
}

export default useFormValidation;
